import { FlowArrow } from '@phosphor-icons/react'
import type { CustomWorkflowTemplate } from '@/lib/api'
import type { WorkflowTemplateCategory, WorkflowTemplateDef } from './workflowTemplates'

/**
 * A gallery card is either a built-in (from workflowTemplates.ts, compiled
 * into the bundle) or a custom one a user saved via "Save as template" in
 * the builder (backend/app/routes/workflow_templates.py, account-scoped).
 * See docs/superpowers/specs/2026-08-21-workflow-templates-design.md.
 */
export interface GalleryTemplate extends WorkflowTemplateDef {
  source: 'builtin' | 'custom'
  /** The backend row id -- only present for custom templates, used to delete. */
  templateId?: number
  createdBy?: string
  /** The creator's user id -- lets the gallery gate delete to "your own"
   * templates for a Rep (whose workflows.manage scope is "own"), while
   * Owner/Admin/Manager ("all" scope) can delete any account template. */
  createdByUserId?: number
}

function flowSummaryFor(nodes: CustomWorkflowTemplate['nodes']): string {
  const labels = nodes.map((n) => n?.data?.label).filter(Boolean)
  return labels.length ? labels.join(' → ') : 'Custom workflow'
}

export function toGalleryTemplate(template: CustomWorkflowTemplate): GalleryTemplate {
  return {
    id: `custom_${template.id}`,
    name: template.name,
    description: template.description,
    category: template.category as WorkflowTemplateCategory,
    icon: FlowArrow,
    tags: [],
    flowSummary: flowSummaryFor(template.nodes),
    nodes: template.nodes,
    edges: template.edges,
    source: 'custom',
    templateId: template.id,
    createdBy: template.created_by,
    createdByUserId: template.created_by_id,
  }
}

/** Custom templates first (most relevant -- a user's own account), then the
 * built-in catalog. Both flow through the same search/filter logic in
 * WorkflowTemplateGallery.tsx unchanged. */
export function mergeWorkflowTemplates(
  custom: CustomWorkflowTemplate[],
  builtins: WorkflowTemplateDef[],
): GalleryTemplate[] {
  return [
    ...custom.map(toGalleryTemplate),
    ...builtins.map((b) => ({ ...b, source: 'builtin' as const })),
  ]
}
